import React, { useContext, useMemo } from 'react';
import { IDishesContext, DishesContext } from '../../../dishesState/dishesContext';
import { UserRoles, IDishesByRestaurantV2 } from 'types';
import { getDishPrice } from 'utils/dishes';
import { useRoleAccess } from 'hooks/useRoleAccess';
import { Link } from 'react-router-dom';
import Text from 'components/Text/Text';
import { ImgUploaderByPreSignedUrl } from 'components/ImgUploader/ImgUploaderByPreSignedUrl';
import { ItemsAsLinkList } from 'components/Lists/ListView/ItemsAsLinkList';
import { Badge } from 'components/Badge';
import DataController from 'components/DataController/DataController';
import classNames from 'classnames';
import style from './Item.module.scss';

interface IProps {
	data: IDishesByRestaurantV2['items'][0];
	index: number;
}

export const Item: React.FC<IProps> = ({ data, index }) => {
	const { setIDForDeletion, setIDForEditModGroupTemplate } = useContext<IDishesContext>(DishesContext);
	const hasEditAccess = useRoleAccess([
		UserRoles.SUPER_USER,
		UserRoles.CLIENT_MANAGER,
		UserRoles.GROUP_MANAGER,
		UserRoles.RESTAURANT_MANAGER,
		UserRoles.TECHNICAL,
	]);
	const dishRowClasses = classNames(style.dish, style.row);
	const dishNameClasses = classNames(style.dishName);
	const articleNumberRowClasses = classNames(style.articleNumber, style.row);
	const categorySubcategoryRowClasses = classNames(style.categorySubcategory, style.row);
	const stopStatusRowClasses = classNames(style.stopStatus, style.row);
	const priceRowClasses = classNames(style.price, style.row);
	const externalIdRowClasses = classNames(style.externalId, style.row);
	const hasPortionsRowClasses = classNames(style.hasPortions, style.row);
	const actionRowClasses = classNames(style.action, style.row);

	const subcategories = useMemo(
		() =>
			data.subCategories.map((subcategory) => ({
				id: subcategory.subCategory.id,
				name: `${subcategory.category.name} - ${subcategory.subCategory.name}`,
			})),
		[data.subCategories],
	);

	const { hasManyPrices, price } = useMemo(() => getDishPrice(data), [data.portionPrices, data.price]);

	const editButtonElement = (
		<div className={style.editItemWrapper}>
			<Link to={`/menu-v2/dishes/edit?dishID=${data.id}`}>
				<Text translationNameSpace="listing" translationKey="editMainInfo" size="s" />
			</Link>
			<Link to={`/menu-v2/dishes/edit-restaurant-properties?dishID=${data.id}`}>
				<Text translationNameSpace="listing" translationKey="editRestaurantsProperties" size="s" />
			</Link>
			<div className={style.linkStyle} onClick={() => setIDForEditModGroupTemplate(data.id)}>
				<Text translationNameSpace="listing" translationKey="editModGroupsTemplate" size="s" />
			</div>
		</div>
	);

	return (
		<div data-test={`Item-${index}`}>
			<div className={style.container}>
				<div className={dishRowClasses}>
					<div className={style.imgWrapper}>
						<ImgUploaderByPreSignedUrl loaderSize="small" preSignedUrl={data.preSignedUrl} />
					</div>
					<Text
						className={dishNameClasses}
						translationKey={data.name}
						withTooltip={true}
						data-test="dishName"
					/>
				</div>
				<Text
					className={articleNumberRowClasses}
					translationNameSpace="form"
					translationKey={data.articleNumber}
					withTooltip={true}
					data-test="articleNumber"
				/>
				<div className={categorySubcategoryRowClasses}>
					<ItemsAsLinkList items={subcategories} link="/menu-v2/subcategory/edit?subcategoryID=" />
				</div>
				<div className={stopStatusRowClasses}>
					<Badge color={data.isDisabled ? 'red' : 'purple'}>
						<Text
							translationNameSpace="listing"
							translationKey={data.isDisabled ? 'inStopLabel' : 'availableMaleLabel'}
							asFragment={true}
							data-test="stopStatus"
						/>
					</Badge>
				</div>
				<Text
					data-test="price"
					className={priceRowClasses}
					translationNameSpace="listing"
					translationKey={hasManyPrices ? 'priceValueWithStartsFrom' : 'priceValue'}
					translationOptions={{
						price: price !== null ? `${data.currencySymbol || ''}${price}` : '-',
					}}
					withTooltip={true}
				/>
				<Text
					className={externalIdRowClasses}
					translationKey={data.externalId}
					withTooltip={true}
					data-test="externalId"
				/>
				<div className={hasPortionsRowClasses}>
					{data.hasPortions ? (
						<Badge color="lightGreen">
							<Text
								translationNameSpace="listing"
								translationKey="enabledLabel"
								asFragment={true}
								data-test="stopStatus"
							/>
						</Badge>
					) : (
						<Text translationKey="-" />
					)}
				</div>
				<div className={actionRowClasses}>
					<DataController
						URLWithIDToInfo={`/menu-v2/dishes/view?dishID=${data.id}`}
						customEditButton={hasEditAccess && editButtonElement}
						onClickDelete={() => setIDForDeletion(data.id)}
						size="smallBtn"
					/>
				</div>
			</div>
		</div>
	);
};
